"use client";

import React, { useEffect, useRef, useState } from "react";
import { Building2, ChartLine, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  title: "Resort Assessment",
  description: "We perform a comprehensive forensic analysis of your timeshare contract to uncover hidden fees and legal misrepresentations.",
  icon: Building2,
  iconColor: "text-secondary"
}, {
  title: "Qualification Review",
  description: "Our legal specialists evaluate your case against industry regulations to determine the most effective path for a permanent exit.",
  icon: RefreshCw,
  iconColor: "text-primary"
}, {
  title: "Strategic Resolution",
  description: "Experience a structured guidance process designed to terminate your ownership and stop the cycle of rising maintenance costs.",
  icon: ChartLine,
  iconColor: "text-secondary"
}];
export default function ThreeColumnFeature() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="172558" id="form" className="bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-secondary/5 rounded-full blur-[120px] pointer-events-none" />

      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16 md:mb-24">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-primary">
            The Nexus Eligibility Framework
          </h2>
          <p className="font-default text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            A premium, professional process designed to help timeshare owners navigate the complexities of contract termination with high-trust guidance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {features.map((feature, i) => <Card key={i} data-index={i} className="relative overflow-hidden bg-card/70 backdrop-blur-md border-border/50 shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:border-secondary/20 group after:absolute after:inset-0 after:rounded-xl after:shadow-[inset_0_0_24px_rgba(15,23,42,0.02)] after:pointer-events-none flex flex-col h-full">
              <CardHeader className="pb-4">
                <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-lg bg-background/80 border border-border/50 shadow-sm group-hover:scale-110 group-hover:border-secondary/30 transition-all duration-500 ease-out">
                  <feature.icon className={`size-7 stroke-[2px] ${feature.iconColor}`} aria-hidden="true" />
                </div>
                <CardTitle className="font-default text-2xl font-bold tracking-tight text-primary">
                  {feature.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <CardDescription className="font-default text-base leading-relaxed text-muted-foreground">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}